import type { DeviceMapping } from '@chukua/common';
import { Segmented, Table } from 'antd';
import { Cable, SearchX, ShieldAlert } from 'lucide-react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { ListSearch } from '../../../../../shared/components/ListSearch';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { useDeviceMappings } from '../hooks/use-device-mappings';
import { buildDeviceMappingColumns } from './build-device-mapping-columns';

const CURRENT_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Current', value: 'true' },
  { label: 'Ended', value: 'false' },
];

/** Which of this integration's devices is on which truck, and over what period; searched, filtered, sorted and paged on the server. */
export function DeviceMappingsTable({ integrationId }: { integrationId: string }) {
  const mappings = useDeviceMappings(integrationId);
  const { list, state } = mappings;

  if (state.status === 'loading') return <TableSkeleton label="Loading device mappings" withSearch />;
  if (state.status === 'error' || state.status === 'forbidden') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load device mappings" description="Something went wrong loading the mappings. Try reloading the page." />
    );
  }
  if (state.page.total === 0 && !list.isFiltered && list.query.current === undefined) {
    return <EmptyState icon={Cable} title="No devices mapped yet" description="Put a device on a truck so its positions count for that truck." />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <ListSearch value={list.query.q} placeholder="Search device or truck" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
        <Segmented
          options={CURRENT_OPTIONS}
          value={list.query.current === undefined ? 'all' : String(list.query.current)}
          onChange={(value) => list.setQuery({ current: value === 'all' ? undefined : value === 'true' })}
        />
      </div>
      <Table<DeviceMapping>
        rowKey="id"
        dataSource={state.page.items}
        columns={buildDeviceMappingColumns({ list, onEnd: mappings.end })}
        loading={mappings.isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: <EmptyState icon={SearchX} title="No matching mappings" description="No mappings match this search or filter." /> }}
        {...list.tableProps<DeviceMapping>(state.page.total)}
      />
    </div>
  );
}
